<template>
    <div class="brevo-newsletter-box">
        <div class="card">
            <div class="card-header bg-info text-white">
                <i class="fas fa-envelope"></i> Newsletter Subscriptions
            </div>
            <div class="card-body">
                <!-- Warning if no email -->
                <div v-if="!hasEmail" class="alert alert-warning" role="alert">
                    <i class="fas fa-exclamation-triangle"></i>
                    <strong>Aggiungi email per gestire newsletter</strong>
                    <p class="mb-0 small">Add an email address to this business card to manage newsletter subscriptions.</p>
                </div>

                <!-- Loading state -->
                <div v-if="loading" class="text-center py-3">
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                    <p class="mt-2 text-muted">Loading newsletter lists...</p>
                </div>

                <!-- Newsletter checkboxes -->
                <div v-if="!loading && newsletters.length > 0">
                    <!-- Newsletter list with scroll -->
                    <div class="newsletter-list">
                        <div class="form-group" v-for="newsletter in paginatedNewsletters" :key="newsletter.id">
                            <div class="form-check">
                                <input
                                    type="checkbox"
                                    class="form-check-input"
                                    :id="`newsletter-${businessCardId}-${newsletter.id}`"
                                    :value="newsletter.id"
                                    v-model="selectedLists"
                                    :disabled="!hasEmail || readonly || saving"
                                >
                                <label
                                    class="form-check-label"
                                    :for="`newsletter-${businessCardId}-${newsletter.id}`"
                                    :class="{'text-muted': !hasEmail || readonly}"
                                >
                                    {{ newsletter.name }}
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Pagination controls -->
                    <div class="pagination-controls mt-3 mb-3">
                        <div class="d-flex justify-content-between align-items-center">
                            <button
                                type="button"
                                @click.prevent="previousPage"
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="currentPage === 1"
                            >
                                <i class="fas fa-chevron-left"></i> Previous
                            </button>
                            <span class="text-muted">
                                Page {{ currentPage }} of {{ totalPages }} ({{ newsletters.length }} total)
                            </span>
                            <button
                                type="button"
                                @click.prevent="nextPage"
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="currentPage === totalPages"
                            >
                                Next <i class="fas fa-chevron-right"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Save button -->
                    <div class="mt-3" v-if="!readonly && hasEmail">
                        <button
                            type="button"
                            @click.prevent="saveSubscriptions"
                            class="btn btn-sm btn-primary btn-block"
                            :disabled="saving || !hasChanges"
                        >
                            <span v-if="saving">
                                <i class="fas fa-spinner fa-spin"></i> Saving...
                            </span>
                            <span v-else>
                                <i class="fas fa-save"></i> Save Subscriptions
                            </span>
                        </button>
                        <small class="text-muted d-block text-center mt-2" v-if="!hasChanges">
                            No changes to save
                        </small>
                    </div>
                </div>

                <!-- No newsletters available -->
                <div v-if="!loading && newsletters.length === 0" class="alert alert-info" role="alert">
                    <i class="fas fa-info-circle"></i>
                    No newsletter lists available. Please check your Brevo configuration.
                </div>

                <!-- Success/Error messages -->
                <div v-if="successMessage" class="alert alert-success mt-3" role="alert">
                    <i class="fas fa-check-circle"></i> {{ successMessage }}
                </div>
                <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
                    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BrevoNewsletterBox',

    props: {
        businessCardId: {
            type: Number,
            required: true
        },
        email: {
            type: String,
            default: null
        },
        readonly: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            newsletters: [],
            selectedLists: [],
            initialLists: [],
            loading: false,
            saving: false,
            successMessage: null,
            errorMessage: null,
            currentPage: 1,
            itemsPerPage: 10
        };
    },

    computed: {
        hasEmail() {
            return this.email && this.email.trim().length > 0;
        },

        hasChanges() {
            // Compare current selection with initial state
            if (this.selectedLists.length !== this.initialLists.length) {
                return true;
            }

            const sorted1 = [...this.selectedLists].sort();
            const sorted2 = [...this.initialLists].sort();

            return !sorted1.every((val, index) => val === sorted2[index]);
        },

        totalPages() {
            return Math.ceil(this.newsletters.length / this.itemsPerPage);
        },

        sortedNewsletters() {
            // Sort newsletters: subscribed ones first, then alphabetically by name
            return [...this.newsletters].sort((a, b) => {
                // First sort by subscribed status (true before false)
                if (a.subscribed !== b.subscribed) {
                    return b.subscribed ? 1 : -1;
                }
                // Then sort alphabetically by name
                return a.name.localeCompare(b.name);
            });
        },

        paginatedNewsletters() {
            const startIndex = (this.currentPage - 1) * this.itemsPerPage;
            const endIndex = startIndex + this.itemsPerPage;
            return this.sortedNewsletters.slice(startIndex, endIndex);
        }
    },

    watch: {
        email: {
            handler(newEmail, oldEmail) {
                // Reload subscriptions if email changes
                if (newEmail !== oldEmail) {
                    this.loadSubscriptions();
                }
            }
        }
    },

    mounted() {
        this.loadSubscriptions();
    },

    methods: {
        async loadSubscriptions() {
            this.loading = true;
            this.errorMessage = null;
            this.successMessage = null;

            try {
                const response = await axios.get(
                    `/api/v1/business-cards/${this.businessCardId}/brevo/subscriptions`
                );

                if (response.data.success) {
                    this.newsletters = response.data.newsletters;

                    // Extract subscribed list IDs
                    this.selectedLists = this.newsletters
                        .filter(n => n.subscribed)
                        .map(n => n.id);

                    // Store initial state for change detection
                    this.initialLists = [...this.selectedLists];
                } else {
                    this.errorMessage = response.data.message || 'Failed to load newsletters';
                }
            } catch (error) {
                console.error('Error loading newsletter subscriptions:', error);
                this.errorMessage = 'Error loading newsletter subscriptions. Please try again.';

                // If it's a 404, the business card doesn't exist
                if (error.response && error.response.status === 404) {
                    this.errorMessage = 'Business card not found.';
                }
            } finally {
                this.loading = false;
            }
        },

        async saveSubscriptions() {
            this.saving = true;
            this.errorMessage = null;
            this.successMessage = null;

            try {
                const response = await axios.post(
                    `/api/v1/business-cards/${this.businessCardId}/brevo/sync`,
                    {
                        list_ids: this.selectedLists
                    }
                );

                if (response.data.success) {
                    this.successMessage = response.data.message;

                    // Update initial state
                    this.initialLists = [...this.selectedLists];

                    // Clear success message after 3 seconds
                    setTimeout(() => {
                        this.successMessage = null;
                    }, 3000);

                    // Emit event for parent component
                    this.$emit('subscriptions-updated', {
                        added: response.data.added,
                        removed: response.data.removed
                    });
                } else {
                    this.errorMessage = response.data.message || 'Failed to update subscriptions';
                }
            } catch (error) {
                console.error('Error saving newsletter subscriptions:', error);

                let message = 'Error saving subscriptions. Please try again.';

                if (error.response) {
                    if (error.response.status === 403) {
                        message = 'You do not have permission to update newsletter subscriptions.';
                    } else if (error.response.data && error.response.data.message) {
                        message = error.response.data.message;
                    }
                }

                this.errorMessage = message;
            } finally {
                this.saving = false;
            }
        },

        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
            }
        },

        previousPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
            }
        }
    }
};
</script>

<style scoped>
.brevo-newsletter-box .card-header {
    font-weight: 600;
}

.brevo-newsletter-box .newsletter-list {
    max-height: 550px;
    overflow-y: auto;
    margin-bottom: 0;
}

.brevo-newsletter-box .form-check {
    padding: 0.3rem 0 0.3rem 1.5rem;
    margin-bottom: 0;
}

.brevo-newsletter-box .form-group {
    margin-bottom: 0;
}

.brevo-newsletter-box .form-check-label {
    cursor: pointer;
    user-select: none;
    font-size: 0.9rem;
}

.brevo-newsletter-box .form-check-input:disabled + .form-check-label {
    cursor: not-allowed;
}

.brevo-newsletter-box .pagination-controls {
    border-top: 1px solid #dee2e6;
    padding-top: 0.75rem;
}

.brevo-newsletter-box .pagination-controls button:disabled {
    opacity: 0.4;
}

.brevo-newsletter-box .alert {
    margin-bottom: 0;
}

.brevo-newsletter-box .alert:last-child {
    margin-top: 1rem;
}
</style>
